import { Button, Flex, Text } from '@radix-ui/themes'
import { FileSpreadsheetIcon, KeyRoundIcon, ShieldCheckIcon, Table2Icon } from 'lucide-react'

import { ADVANCED_EXPORTS_ROLE_LABEL, ADVANCED_EXPORT_REPORTS } from '../../data/advancedExports'
import { AnnouncementBody, useJourney } from '../../journey'

import { advancedExportsSpec } from './journey'
import { Simulator } from './Simulator'

import type { AnnouncementProps } from '../types'

export const Body = ({ active, onDismiss }: AnnouncementProps) => {
  const journey = useJourney(advancedExportsSpec, active)
  const gateSatisfied = journey.spotlight !== 'pending'

  return (
    <AnnouncementBody
      heading="Del permiso a la planilla"
      blurb="Primero se habilita el permiso; después cada reporte queda a un clic de distancia en formato Excel."
      caption={journey.caption}
      stage={<Simulator state={journey} />}
      story={
        <>
          <Flex align="center" gap="2" className="mb-2 text-[#107c41]">
            <FileSpreadsheetIcon className="h-4 w-4" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-wider">Característica destacada</span>
          </Flex>

          <p className="text-[15px] font-bold leading-snug text-[#1c1917]">Exportación Avanzada de Reportes</p>
          <p className="mt-1 text-xs leading-relaxed text-[#706d71]">
            Cinco reportes de su automotora, descargables en planillas Excel listas para trabajar.
          </p>

          <div className="vk-gate mt-3.5 rounded-lg p-3" data-state={gateSatisfied ? 'satisfied' : 'pending'}>
            <Flex align="start" gap="2">
              <span
                className={`shrink-0 rounded p-1 ${
                  gateSatisfied ? 'bg-[#107c41]/10 text-[#107c41]' : 'bg-[#e5484d]/10 text-[#e5484d]'
                }`}
              >
                {gateSatisfied ? <ShieldCheckIcon className="h-3.5 w-3.5" /> : <KeyRoundIcon className="h-3.5 w-3.5" />}
              </span>
              <div>
                <p className="text-[11px] font-bold leading-tight text-[#1c1917]">Primer paso: habilite el permiso</p>
                <p className="mt-1 text-[11px] leading-snug text-[#706d71]">
                  Sin el permiso{' '}
                  <span className="font-mono font-bold text-[#1c1917]">{ADVANCED_EXPORTS_ROLE_LABEL}</span> la pestaña{' '}
                  <strong className="text-[#1c1917]">Exportación Avanzada</strong> no se muestra. Una vez habilitado,
                  aparece <strong className="text-[#1c1917]">primera en Reportes</strong> y queda seleccionada al
                  entrar. Actívelo en <strong className="text-[#1c1917]">Usuarios</strong>, editando el perfil, o
                  solicítelo a su administrador.
                </p>
              </div>
            </Flex>
          </div>

          <Flex align="start" gap="2" className="mt-3 text-[11px] leading-snug text-[#706d71]">
            <Table2Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#107c41]" />
            <p>
              Cada archivo llega con <strong className="text-[#1c1917]">encabezados congelados</strong>,{' '}
              <strong className="text-[#1c1917]">columnas autoajustadas</strong> y{' '}
              <strong className="text-[#1c1917]">filtros activos</strong> en todas las columnas.
            </p>
          </Flex>

          <div className="mt-3.5 border-t border-[#e4e1e4] pt-3">
            <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-[#706d71]">
              Los cinco reportes
            </p>
            <div className="space-y-1">
              {ADVANCED_EXPORT_REPORTS.map((report, index) => {
                const Icon = report.icon

                return (
                  <div
                    key={report.id}
                    className="vk-info-block rounded-r py-1.5 pl-2.5 pr-2"
                    data-active={journey.spotlight === index}
                  >
                    <Flex align="start" gap="2">
                      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#107c41]" />
                      <div>
                        <p className="text-[11px] font-bold leading-tight text-[#1c1917]">{report.title}</p>
                        <p className="text-[11px] leading-snug text-[#706d71]">{report.description}</p>
                      </div>
                    </Flex>
                  </div>
                )
              })}
            </div>
          </div>

          <Flex justify="end" className="mt-auto pt-4">
            <Button size="2" color="green" onClick={onDismiss}>
              <Text weight="bold">Comenzar a explorar</Text>
            </Button>
          </Flex>
        </>
      }
    />
  )
}
